/**
 * HslObjectVariant - Variante HSL Object para Color
 *
 * Representa cor como objeto { h, s, l, a? }
 *
 * @example
 * { h: 0, s: 100, l: 50 }        // Red
 * { h: 0, s: 100, l: 50, a: 0.5 } // Semi-transparent red
 */

import { BaseVariant } from "../../../contracts";
import type { ValidationResult, FormatOptions } from "../../../types";
import type { RGBA, ColorHslObject, ColorFormatOptions } from "../types";
import {
  NAMED_COLORS,
  normalizeAlpha,
  rgbToHsl,
  hslToRgb,
  clamp,
} from "../types";

export class HslObjectVariant extends BaseVariant<ColorHslObject, RGBA> {
  readonly name = "hsl_object";
  readonly type = "object" as const;

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  toBase(value: ColorHslObject): RGBA {
    const h = clamp(value.h, 0, 360);
    const s = clamp(value.s, 0, 100);
    const l = clamp(value.l, 0, 100);
    const a = normalizeAlpha(value.a);

    const rgb = hslToRgb(h, s, l);

    return {
      r: rgb.r,
      g: rgb.g,
      b: rgb.b,
      a,
    };
  }

  fromBase(base: RGBA): ColorHslObject {
    const hsl = rgbToHsl(base.r, base.g, base.b);

    const result: ColorHslObject = {
      h: hsl.h,
      s: hsl.s,
      l: hsl.l,
    };

    // Only include alpha if not 1
    if (base.a !== undefined && base.a < 1) {
      result.a = normalizeAlpha(base.a);
    }

    return result;
  }

  // ===========================================================================
  // CAST
  // ===========================================================================

  cast(input: unknown): ColorHslObject | null {
    // Already an HSL object
    if (
      typeof input === "object" &&
      input !== null &&
      "h" in input &&
      "s" in input &&
      "l" in input
    ) {
      const obj = input as { h: unknown; s: unknown; l: unknown; a?: unknown };

      if (
        typeof obj.h !== "number" ||
        typeof obj.s !== "number" ||
        typeof obj.l !== "number"
      ) {
        return null;
      }

      const result: ColorHslObject = {
        h: clamp(Math.round(obj.h), 0, 360),
        s: clamp(Math.round(obj.s), 0, 100),
        l: clamp(Math.round(obj.l), 0, 100),
      };

      if (typeof obj.a === "number") {
        result.a = normalizeAlpha(obj.a);
      }

      return result;
    }

    // RGB object
    if (
      typeof input === "object" &&
      input !== null &&
      "r" in input &&
      "g" in input &&
      "b" in input
    ) {
      const obj = input as { r: number; g: number; b: number; a?: number };
      return this.fromBase({
        r: obj.r,
        g: obj.g,
        b: obj.b,
        a: obj.a ?? 1,
      });
    }

    // String formats
    if (typeof input === "string") {
      const trimmed = input.trim().toLowerCase();

      // Named color
      if (NAMED_COLORS[trimmed]) {
        return this.fromBase(NAMED_COLORS[trimmed]);
      }

      // Hex format
      const hexMatch = trimmed.match(
        /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i,
      );
      if (hexMatch) {
        return this.fromBase({
          r: parseInt(hexMatch[1], 16),
          g: parseInt(hexMatch[2], 16),
          b: parseInt(hexMatch[3], 16),
          a: hexMatch[4] ? parseInt(hexMatch[4], 16) / 255 : 1,
        });
      }

      // Shorthand hex
      const hexShortMatch = trimmed.match(
        /^#?([0-9a-f])([0-9a-f])([0-9a-f])([0-9a-f])?$/i,
      );
      if (hexShortMatch) {
        return this.fromBase({
          r: parseInt(hexShortMatch[1] + hexShortMatch[1], 16),
          g: parseInt(hexShortMatch[2] + hexShortMatch[2], 16),
          b: parseInt(hexShortMatch[3] + hexShortMatch[3], 16),
          a: hexShortMatch[4]
            ? parseInt(hexShortMatch[4] + hexShortMatch[4], 16) / 255
            : 1,
        });
      }

      // rgb()/rgba() string
      const rgbMatch = trimmed.match(
        /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/,
      );
      if (rgbMatch) {
        return this.fromBase({
          r: parseInt(rgbMatch[1], 10),
          g: parseInt(rgbMatch[2], 10),
          b: parseInt(rgbMatch[3], 10),
          a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1,
        });
      }

      // hsl()/hsla() string
      const hslMatch = trimmed.match(
        /^hsla?\s*\(\s*(\d+)\s*,\s*(\d+)%?\s*,\s*(\d+)%?\s*(?:,\s*([\d.]+))?\s*\)$/,
      );
      if (hslMatch) {
        const result: ColorHslObject = {
          h: clamp(parseInt(hslMatch[1], 10), 0, 360),
          s: clamp(parseInt(hslMatch[2], 10), 0, 100),
          l: clamp(parseInt(hslMatch[3], 10), 0, 100),
        };
        if (hslMatch[4]) {
          result.a = normalizeAlpha(parseFloat(hslMatch[4]));
        }
        return result;
      }
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  validate(value: ColorHslObject): ValidationResult {
    const errors: string[] = [];

    if (typeof value !== "object" || value === null) {
      errors.push("HSL color must be an object");
      return { valid: false, errors };
    }

    if (!("h" in value) || !("s" in value) || !("l" in value)) {
      errors.push("HSL color must have h, s, l properties");
      return { valid: false, errors };
    }

    if (
      typeof value.h !== "number" ||
      typeof value.s !== "number" ||
      typeof value.l !== "number"
    ) {
      errors.push("h, s, l must be numbers");
      return { valid: false, errors };
    }

    if (value.h < 0 || value.h > 360) {
      errors.push("h (hue) must be between 0 and 360");
    }
    if (value.s < 0 || value.s > 100) {
      errors.push("s (saturation) must be between 0 and 100");
    }
    if (value.l < 0 || value.l > 100) {
      errors.push("l (lightness) must be between 0 and 100");
    }

    if (value.a !== undefined) {
      if (typeof value.a !== "number") {
        errors.push("a must be a number");
      } else if (value.a < 0 || value.a > 1) {
        errors.push("a must be between 0 and 1");
      }
    }

    return { valid: errors.length === 0, errors };
  }

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  format(value: ColorHslObject, options?: FormatOptions): string {
    const opts = options as ColorFormatOptions | undefined;

    const h = clamp(Math.round(value.h), 0, 360);
    const s = clamp(Math.round(value.s), 0, 100);
    const l = clamp(Math.round(value.l), 0, 100);
    const a = value.a !== undefined ? normalizeAlpha(value.a) : 1;

    if (opts?.includeAlpha || a < 1) {
      if (opts?.compact) {
        return `hsla(${h},${s}%,${l}%,${a})`;
      }
      return `hsla(${h}, ${s}%, ${l}%, ${a})`;
    }

    if (opts?.compact) {
      return `hsl(${h},${s}%,${l}%)`;
    }
    return `hsl(${h}, ${s}%, ${l}%)`;
  }
}
